import React, { useState } from "react";

import { ConsentModel, invalidateQuery } from "@/api";
import { publishConfigVersion } from "@/consent-management/api/publishConfigVersion";
import { Banner } from "@/design-system/components/Banner/Banner";
import { Checkbox } from "@/design-system/components/Checkbox/Checkbox";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Typography } from "@/design-system/components/Typography/Typography";
import { unpackRequestErrorMessage } from "@/utils/request";
import { WizardLink } from "@/wizards/framework/WizardLink";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import { ConsentLoadError } from "../ConsentLoadError";
import {
  countUnpublishedChanges,
  findPublishBlockers,
  findUncategorized,
} from "../draftStatus";
import { summarizeTargets } from "../targeting";
import { ConsentManagerStepProps } from "../types";
import {
  freshpaintConsent,
  LOAD_ERROR_REASON,
  NO_WRITE_ACCESS_REASON,
  useFreshpaintConsent,
} from "../useFreshpaintConsent";

export function PublishStep({ goNext, goToStep }: ConsentManagerStepProps) {
  const {
    draft,
    lastPublished,
    destinations,
    canWrite,
    isLoading,
    loadError,
    retry,
    audiencesEnabled,
    sessionReplayEntitled,
  } = useFreshpaintConsent();
  const [promptAgain, setPromptAgain] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unpublishedChanges = draft
    ? countUnpublishedChanges(draft, lastPublished)
    : 0;
  const blockers = draft
    ? findPublishBlockers({
        draft,
        destinations,
        audiencesEnabled,
        sessionReplayEntitled,
      })
    : [];
  const privacyPolicyUrl: string =
    draft?.freshpaintConfig?.privacyPolicyUrl ?? "";
  const allTargets = summarizeTargets(draft);
  const targets = allTargets.filter((target) => !target.combined);
  const combinedCount = allTargets.length - targets.length;
  const modelLabel = (model: ConsentModel) =>
    model === "opt-in" ? "Opt-in" : "Opt-out";

  async function publish() {
    setPublishing(true);
    setError(null);
    try {
      await publishConfigVersion({
        type: freshpaintConsent.slug,
        incrementRevision: promptAgain,
        publishDescription: "Published from the Consent Manager wizard",
      });
      await invalidateQuery(["get_consent_management_configs", {}]);
      goNext();
    } catch (publishError) {
      setError(unpackRequestErrorMessage(publishError));
      setPublishing(false);
    }
  }

  if (!isLoading && draft && unpublishedChanges === 0) {
    return (
      <WizardStepLayout
        title="Your banner is published"
        description="The published version matches your draft, so there’s nothing new to publish."
        primaryAction={{ label: "Continue", onClick: goNext }}
      />
    );
  }

  return (
    <WizardStepLayout
      title="Publish your banner"
      description={
        targets.length > 0
          ? "Publishing makes this version, with every targeted rule below, the one your sites load once the Consent Manager is on."
          : "Publishing makes this version the one your site loads once the Consent Manager is on."
      }
      primaryAction={{
        label: "Publish",
        onClick: publish,
        loading: publishing,
        disabled:
          isLoading ||
          loadError ||
          !draft ||
          blockers.length > 0 ||
          canWrite === false,
        disabledReason: loadError
          ? LOAD_ERROR_REASON
          : isLoading || !draft
            ? undefined
            : canWrite === false
              ? NO_WRITE_ACCESS_REASON
              : "Fix the problems listed above first",
      }}
    >
      {loadError ? (
        <ConsentLoadError retry={retry} />
      ) : isLoading || !draft ? (
        <LoadingSpinner className="size-6" />
      ) : (
        <>
          <dl className="border-borderSubtle divide-borderSubtle divide-y rounded-lg border">
            {[
              {
                label:
                  targets.length > 0
                    ? "Consent model where no rule matches"
                    : "Consent model",
                value: modelLabel(draft.consentModel),
              },
              ...(draft.freshpaintConfig?.geotargetingBehavior === "managed"
                ? [
                    {
                      label: "Regional rules",
                      value: "Opt-in in the EU, UK and US privacy states",
                    },
                  ]
                : []),
              {
                label: "Destinations categorized",
                value: `${
                  destinations.length -
                  findUncategorized(destinations, draft.consentMapping).length
                } of ${destinations.length}`,
              },
              {
                label: "Privacy policy",
                value: privacyPolicyUrl || "Not linked",
              },
              {
                label: "Changes since the last publish",
                value: lastPublished
                  ? String(unpublishedChanges)
                  : "First publish",
              },
            ].map(({ label, value }) => (
              <div
                key={label}
                className="flex items-center justify-between gap-4 px-4 py-2"
              >
                <dt className="text-body-sm text-textTertiary">{label}</dt>
                <dd className="text-label-sm text-textPrimary truncate">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
          {targets.length > 0 && (
            <section className="flex flex-col gap-2">
              <Typography variant="label-md" as="h3">
                {targets.length} targeted{" "}
                {targets.length === 1 ? "rule" : "rules"}
              </Typography>
              <ul className="border-borderSubtle divide-borderSubtle divide-y rounded-lg border">
                {targets.map((target) => (
                  <li
                    key={target.id}
                    className="flex items-center justify-between gap-4 px-4 py-2"
                  >
                    <div className="flex min-w-0 flex-col">
                      <span className="text-label-sm text-textPrimary truncate">
                        {target.label}
                      </span>
                      {target.matches !== target.label && (
                        <span className="text-body-sm text-textTertiary truncate">
                          {target.matches}
                        </span>
                      )}
                    </div>
                    <span className="text-label-sm text-textPrimary shrink-0">
                      {target.bannerOff
                        ? "Banner off"
                        : modelLabel(target.consentModel)}
                    </span>
                  </li>
                ))}
              </ul>
              {combinedCount > 0 && (
                <Typography variant="body-sm" color="subtle">
                  On your opt-out sites, visitors from opt-in regions still get
                  opt-in.
                </Typography>
              )}
            </section>
          )}
          {targets.some((target) => target.bannerOff) && (
            <Banner type="warning">
              Rules marked Banner off were turned off on the Consent Manager
              page. Visitors they match see no banner, and Freshpaint sends
              their events to every destination.
            </Banner>
          )}
          {blockers.length > 0 && (
            <Banner type="error">
              <div className="flex flex-col gap-1">
                <span>
                  Your draft can’t be published until{" "}
                  {blockers.length === 1 ? "this is fixed" : "these are fixed"}:
                </span>
                <ul className="list-disc pl-5">
                  {blockers.map((blocker) => (
                    <li key={blocker}>{blocker}</li>
                  ))}
                </ul>
                <span>
                  Fix {blockers.length === 1 ? "it" : "them"} on the{" "}
                  <WizardLink
                    to={freshpaintConsent.configPageUrl()}
                    className="cursor-pointer underline"
                  >
                    Consent Manager page
                  </WizardLink>
                  , then come back.
                </span>
              </div>
            </Banner>
          )}
          {!privacyPolicyUrl && (
            <Banner type="warning">
              Your banner won’t link to a privacy policy, so visitors can’t read
              how you use their data before they choose.{" "}
              <button
                type="button"
                className="cursor-pointer underline"
                onClick={() => goToStep("banner")}
              >
                Link one
              </button>
            </Banner>
          )}
          {lastPublished && (
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={promptAgain}
                onCheckedChange={(checked) => setPromptAgain(checked === true)}
              />
              <Typography variant="body-sm">
                Ask visitors who already responded to the banner to choose again
              </Typography>
            </label>
          )}
        </>
      )}
      {error && <Banner type="error">{error}</Banner>}
    </WizardStepLayout>
  );
}
